import { useQueryState } from 'nuqs';
import { useEffect, useRef, useState } from 'react';
import { isEmptyString } from '@/utils/string';

export type UseQueryParamActionOptions = {
  /**
   * When false, the action is deferred until this becomes true.
   * Use when the action depends on async data (e.g. a query that must finish loading first).
   */
  enabled?: boolean;
};

/**
 * Executes an action once when a query parameter is present, then clears the parameter.
 *
 * By default the action runs after the first commit. Pass `enabled: false` until
 * prerequisites are ready (e.g. `enabled: !isLoading`) so the action runs only once
 * those conditions are satisfied.
 *
 * This is useful for triggering modals or other side effects based on URL params
 * without re-triggering on subsequent renders.
 *
 * @param paramName - The query parameter name
 * @param action - Function to execute with the query param value
 *
 * @example
 * useQueryParamAction('support', () => {
 *   openSupportForm();
 * }); // Triggers when ?support=true (or any value), ignores the value
 *
 * @example
 * useQueryParamAction('ai-message', (message) => {
 *   openChatWithMessage(message);
 * }); // Triggers when ?ai-message=anything and passes the value
 *
 * @example
 * useQueryParamAction('pause_id', handler, { enabled: !isLoading });
 */
export function useQueryParamAction(
  parameterName: string,
  action: (value: string) => void,
  options?: UseQueryParamActionOptions,
): void {
  const [parameterValue, setParameterValue] = useQueryState(parameterName);

  // Capture initial value once on mount
  const [initialValue] = useState(() => parameterValue ?? '');
  const triggered = useRef(false);
  const actionRef = useRef(action);
  actionRef.current = action;
  const enabled = options?.enabled ?? true;

  useEffect(() => {
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- existing effect pattern before this rule was enforced strictly; avoid adding new cases
    if (!enabled || isEmptyString(initialValue) || triggered.current) return;
    triggered.current = true;
    void setParameterValue(null);
    actionRef.current(initialValue);
  }, [enabled, initialValue, setParameterValue]);
}
